import { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { Calendar, X } from 'lucide-react';
import { supabase } from '../../lib/supabase';

const DISMISS_KEY = 'sticky_cta_dismissed_at';
const DISMISS_DURATION_MS = 30 * 60 * 1000;

function getSessionId(): string {
  let id = sessionStorage.getItem('cta_session_id');
  if (!id) {
    id = Math.random().toString(36).slice(2);
    sessionStorage.setItem('cta_session_id', id);
  }
  return id;
}

function trackCtaEvent(eventType: string) {
  supabase.from('mobile_cta_events').insert({
    event_type: eventType,
    page: window.location.pathname,
    user_agent: navigator.userAgent.slice(0, 250),
    session_id: getSessionId(),
  }).then(() => {});
}

export default function StickyMobileCTA() {
  const [isVisible, setIsVisible] = useState(false);
  const location = useLocation();
  const [isDismissed, setIsDismissed] = useState(() => {
    if (typeof localStorage === 'undefined') return false;
    const stored = localStorage.getItem(DISMISS_KEY);
    if (!stored) return false;
    return Date.now() - Number(stored) < DISMISS_DURATION_MS;
  });

  useEffect(() => {
    let shownOnce = false;
    const handleScroll = () => {
      const focused = document.activeElement;
      const editing = focused instanceof HTMLElement && focused.matches('input, textarea, select, [contenteditable="true"]');
      const mobile = window.matchMedia('(max-width: 1023px)').matches;
      if (mobile && !editing && window.scrollY > 300 && !isDismissed) {
        if (!shownOnce) {
          shownOnce = true;
          trackCtaEvent('sticky_shown');
        }
        setIsVisible(true);
      } else {
        setIsVisible(false);
      }
    };

    window.addEventListener('scroll', handleScroll, { passive: true });
    window.addEventListener('resize', handleScroll);
    document.addEventListener('focusin', handleScroll);
    document.addEventListener('focusout', handleScroll);
    window.visualViewport?.addEventListener('resize', handleScroll);
    handleScroll();
    return () => {
      window.removeEventListener('scroll', handleScroll);
      window.removeEventListener('resize', handleScroll);
      document.removeEventListener('focusin', handleScroll);
      document.removeEventListener('focusout', handleScroll);
      window.visualViewport?.removeEventListener('resize', handleScroll);
    };
  }, [isDismissed, location.pathname]);

  useEffect(() => {
    document.body.classList.toggle('pbs-booking-bar-visible', isVisible);
    return () => document.body.classList.remove('pbs-booking-bar-visible');
  }, [isVisible]);

  const handleDismiss = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    trackCtaEvent('sticky_dismissed');
    localStorage.setItem(DISMISS_KEY, String(Date.now()));
    setIsDismissed(true);
    setIsVisible(false);
  };

  return (
    <AnimatePresence>
      {isVisible && (
        <motion.div
          initial={{ y: 100, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          exit={{ y: 100, opacity: 0 }}
          transition={{ type: 'spring', damping: 25, stiffness: 300 }}
          className="lg:hidden fixed bottom-0 left-0 right-0 z-[45]"
        >
          <div className="relative bg-white border-t border-gray-200 shadow-lg pb-safe">
            

            <div className="relative px-4 py-2">
              <div className="flex items-center gap-2">
                {/* Booking is the primary mobile action. */}
                <Link
                  to="/book"
                  onClick={() => trackCtaEvent('sticky_book_clicked')}
                  className="group relative overflow-hidden flex-1 flex items-center justify-center gap-2 px-4 py-3 rounded-xl bg-gray-950 text-white font-bold text-sm shadow-lg min-h-[48px] touch-manipulation border-2 border-black/40"
                >
                  <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/10 to-transparent -skew-x-12 translate-x-[-200%] group-active:translate-x-[200%] transition-transform duration-700" />
                  <Calendar className="w-4 h-4 relative z-10 flex-shrink-0" />
                  <span className="relative z-10 tracking-wide">Book a Call</span>
                </Link>

                {/* Dismiss Button */}
                <button
                  onClick={handleDismiss}
                  className="flex-shrink-0 p-3 rounded-lg bg-gray-100 text-gray-600 active:bg-gray-200 min-h-[48px] min-w-[48px] flex items-center justify-center touch-manipulation"
                  aria-label="Dismiss"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>


            </div>
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
